import { useEffect, useState } from "react"
import { fetchNotificationSettings, updateEmailCategories, updateNotificationSettings } from "../lib/api"
import ThresholdEditor from "./alerts/ThresholdEditor"
import { browserTimeZone } from "../lib/timezone"

// The Notifications part of Profile (#304, spec D3): one on/off per kind of
// scan email. Each part shows only while its email can reach this account
// (`features` from the server), so nobody sets up an email that never sends.
// The whole section is hidden when no part is on.
//
// Free on every plan. What the emails show obeys the per-code 50-scan rule on
// the server; nothing here is gated.
//
// Each toggle is optimistic with a rollback, like the marketing toggle above
// it. The server's answer wins: a suppressed (bounced) address stays off.
export default function NotificationsSection() {
  const [settings, setSettings] = useState(null)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetchNotificationSettings()
      .then((data) => { if (!cancelled) setSettings(data) })
      .catch(() => { /* The section stays hidden. The rest of Profile still works. */ })
    return () => { cancelled = true }
  }, [])

  if (!settings) return null
  const features = settings.features || {}
  if (!features.scan_alerts && !features.quiet_alerts && !features.weekly_digest) return null

  // Each save also sends the browser's time zone, so the weekly summary
  // comes on Monday morning local time (#304, section C).
  async function saveSettings(attrs) {
    setSaving("settings")
    setError(null)
    try {
      setSettings(await updateNotificationSettings({ ...attrs, timezone: browserTimeZone() }))
    } catch (e) {
      setError(e.message || "Couldn't save that. Try again.")
    } finally {
      setSaving(null)
    }
  }

  async function toggleCategory(category) {
    const before = settings.categories[category]
    setSettings((s) => ({ ...s, categories: { ...s.categories, [category]: !before } }))
    setSaving(category)
    setError(null)
    try {
      const result = await updateEmailCategories({ [category]: !before })
      setSettings((s) => ({ ...s, categories: result.categories }))
    } catch (e) {
      setSettings((s) => ({ ...s, categories: { ...s.categories, [category]: before } }))
      setError(e.message || "Couldn't save that. Try again.")
    } finally {
      setSaving(null)
    }
  }

  return (
    <section className="border border-[color:var(--color-border)] dark:border-storm rounded-lg bg-white dark:bg-midnight p-5 mb-5">
      <div className="text-xs uppercase tracking-wide text-[color:var(--color-muted)] dark:text-muted-light">
        Notifications
      </div>

      {features.weekly_digest && (
        <CategoryToggle
          id="notify-weekly-digest"
          label="Weekly scan summary"
          checked={settings.digest_frequency === "weekly"}
          disabled={saving === "settings"}
          onChange={() => saveSettings({ digest_frequency: settings.digest_frequency === "weekly" ? "off" : "weekly" })}
        >
          Each Monday, the scans on your codes the week before. Weeks with no scans send nothing.
        </CategoryToggle>
      )}

      {features.scan_alerts && (
        <CategoryToggle
          id="notify-scan-alerts"
          label="Scan alerts"
          checked={!!settings.categories.scan_alerts}
          disabled={saving === "scan_alerts"}
          onChange={() => toggleCategory("scan_alerts")}
        >
          {scanAlertsHelp(features)}
        </CategoryToggle>
      )}

      {features.scan_alert_rules && settings.categories.scan_alerts && (
        <div className="mt-3 pl-0.5">
          <div className="text-sm font-medium text-ink dark:text-cloud mb-1.5">Scan targets for every code</div>
          <ThresholdEditor
            id="account-scan-targets"
            values={settings.scan_alert_thresholds}
            disabled={saving === "settings"}
            onChange={(values) => saveSettings({ scan_alert_thresholds: values })}
          />
          <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-2">
            A group or a code can have its own targets. Set them in Groups, or on the code's page.
          </p>
        </div>
      )}

      {features.quiet_alerts && (
        <CategoryToggle
          id="notify-quiet-alerts"
          label="No-scan alerts"
          checked={!!settings.categories.quiet_alerts}
          disabled={saving === "quiet_alerts"}
          onChange={() => toggleCategory("quiet_alerts")}
        >
          An email when nobody has scanned a new code yet, or when a code's scans stop. One email for each quiet stretch.
        </CategoryToggle>
      )}

      {features.quiet_alerts && settings.categories.quiet_alerts && (
        <div className="mt-3 pl-0.5 grid gap-2 sm:grid-cols-2">
          <DaysSelect
            id="never-scanned-days"
            label="A new code with no scans after"
            value={settings.never_scanned_days}
            choices={settings.day_choices}
            disabled={saving === "settings"}
            onChange={(days) => saveSettings({ never_scanned_days: days })}
          />
          <DaysSelect
            id="quiet-days"
            label="A code with no new scans for"
            value={settings.quiet_days}
            choices={settings.day_choices}
            disabled={saving === "settings"}
            onChange={(days) => saveSettings({ quiet_days: days })}
          />
          {features.quiet_alerts_gated && (
            <p className="sm:col-span-2 text-xs text-[color:var(--color-muted)] dark:text-muted-light">
              On the free plan, a code past 50 scans shows its total only, so no-scan alerts stop for that code.
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="border border-fail rounded p-3 text-fail text-sm mt-4">{error}</div>
      )}
    </section>
  )
}

function scanAlertsHelp(features) {
  if (!features.scan_alert_rules) return "An email when one of your codes gets to 40 scans, and again at 50."
  if (features.scan_milestones) {
    return "An email when a code gets to a scan target you set. This also stops the emails at 40 and 50 scans."
  }
  return "An email when a code gets to a scan target you set."
}

function DaysSelect({ id, label, value, choices, disabled, onChange }) {
  // The saved value is always offered, even if it isn't one of the usual
  // choices (the API takes any whole number of days in range).
  const options = [...new Set([...(choices || []), value])].sort((a, b) => a - b)
  return (
    <label htmlFor={id} className="text-sm text-ink dark:text-cloud">
      <span className="block mb-1">{label}</span>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="border border-[color:var(--color-border)] dark:border-storm rounded px-2.5 py-1.5 text-sm bg-white dark:bg-midnight text-ink dark:text-cloud focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 dark:focus-visible:ring-offset-midnight disabled:opacity-50"
      >
        {options.map((days) => <option key={days} value={days}>{days} days</option>)}
      </select>
    </label>
  )
}

function CategoryToggle({ id, label, checked, disabled, onChange, children }) {
  return (
    <div className="flex items-start justify-between gap-4 mt-3">
      <div>
        <label htmlFor={id} className="font-display font-semibold text-ink dark:text-cloud block cursor-pointer">
          {label}
        </label>
        <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1">{children}</p>
      </div>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="mt-1 h-5 w-5 flex-shrink-0 accent-[color:var(--color-voltage)] cursor-pointer disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 dark:focus-visible:ring-offset-midnight"
      />
    </div>
  )
}
